import React from 'react';
import PageHeader from '../components/ui/PageHeader';
import CTASection from '../components/home/CTASection';

export default function PrivacyPolicy() {
  const effectiveDate = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-slate-950">
      <PageHeader 
        title="Privacy Policy" 
        subtitle="How we collect, use, and protect your information." 
      />

      <div className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-slate-300">
          
          <div className="mb-12 border-b border-slate-800 pb-8">
            <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-2">Privacy Policy</h2>
            <p className="font-bold text-blue-500 uppercase tracking-widest text-sm mb-2">A.R.'s Elite Detailing</p>
            <p className="text-slate-500 text-sm font-medium">Effective Date: {effectiveDate}</p>
          </div>

          <div className="space-y-12">
            <div>
              <p className="text-lg leading-relaxed text-slate-300">
                A.R.'s Elite Detailing values your privacy. This Privacy Policy explains how we collect, use, and protect the information you provide through our website, booking system, quote forms, and other communications.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">1. Information We Collect</h3>
              <p className="mb-4 leading-relaxed text-slate-400">We may collect the following personal information when you request a quote or book a service:</p>
              <ul className="list-none space-y-3 mb-6 text-slate-400">
                {[
                  "Name, email address, and phone number",
                  "Service location or home address",
                  "Vehicle make, model, year, and condition",
                  "Payment information (processed securely via third-party providers)",
                  "Any additional details you provide through forms or direct messages"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">2. How We Use Your Information</h3>
              <p className="mb-4 leading-relaxed text-slate-400">We use the information we collect to:</p>
              <ul className="list-none space-y-3 text-slate-400">
                {[
                  "Provide accurate quotes and schedule detailing appointments",
                  "Communicate regarding your service, delays, or arrival times",
                  "Process payments for services rendered",
                  "Send appointment reminders and follow-up quality checks",
                  "Improve our website, advertising, and overall customer experience"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">3. SMS & Communication Policy</h3>
              <p className="leading-relaxed text-slate-400">
                By providing your phone number during the booking or quote process, you consent to receive text messages (SMS) and phone calls from A.R.'s Elite Detailing regarding your appointment, service updates, and occasional promotional offers. Standard message and data rates may apply. You may opt out of promotional messages at any time by replying "STOP".
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">4. Media & Photography</h3>
              <p className="leading-relaxed text-slate-400">
                We take pride in our work and frequently take "before and after" photos or videos of the vehicles we detail for quality assurance and marketing purposes (social media, website gallery). We will never capture or share license plates, personal belongings, or identifiable locations without your explicit permission. If you prefer your vehicle not be photographed, please inform us prior to your service.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">5. Sharing of Information</h3>
              <p className="mb-4 leading-relaxed font-semibold text-slate-200">A.R.'s Elite Detailing does not sell or rent your personal information to third parties.</p>
              <p className="mb-4 leading-relaxed text-slate-400">We may share your information only when necessary to operate our business, such as with trusted software providers used for:</p>
              <ul className="list-none space-y-3 mb-6 text-slate-400">
                {[
                  "Appointment scheduling and CRM management (e.g., Urable)",
                  "Secure payment processing",
                  "Website analytics and marketing performance tracking"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">6. Data Security</h3>
              <p className="leading-relaxed text-slate-400">
                We take reasonable administrative and technical precautions to protect your personal information from unauthorized access, loss, or misuse. However, no online data transmission can be guaranteed to be 100% secure.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">7. Your Rights & Choices</h3>
              <p className="mb-4 leading-relaxed text-slate-400">You have the right to:</p>
              <ul className="list-none space-y-3 text-slate-400">
                {[
                  "Request access to the personal data we hold about you",
                  "Request corrections to any inaccurate information",
                  "Request the deletion of your personal data (subject to legal and business record-keeping requirements)",
                  "Opt out of marketing communications"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-8 rounded-sm mt-12">
              <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-4">Contact Information</h3>
              <p className="mb-6 leading-relaxed text-slate-400">If you have any questions or concerns about this Privacy Policy, please contact us:</p>
              
              <div className="space-y-2">
                <p className="font-bold text-white text-lg">A.R.'s Elite Detailing</p>
                <p className="text-slate-400">
                  <span className="text-slate-500 uppercase tracking-widest text-xs font-bold mr-2">Phone:</span>
                  <a href="tel:9048312184" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">904-831-2184</a>
                </p>
                <p className="text-slate-400">
                  <span className="text-slate-500 uppercase tracking-widest text-xs font-bold mr-2">Email:</span>
                  <a href="mailto:booking@arelitedetailing.com" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">booking@arelitedetailing.com</a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      <CTASection />
    </div>
  );
}